import { useCallback, useEffect, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import map from 'lodash/map';

import { UUID } from '../../../../../../../types';

import { useCachePaginatedTeams } from '../../../../../hooks/useCachePaginatedTeams';

import {
  FETCH_CACHE_TEAMS_FILTER_QUERY,
  FetchCacheTeamsFilterQueryResponse
} from '../../../../../queries/fetchCacheTeamsFilter.query';

interface TeamsFilterOptions {
  teamType: string;
  value: UUID[];
}

function useTeamsFilter({ teamType, value }: TeamsFilterOptions) {
  const { teams, teamsError, teamsLoading, filterTeams } =
    useCachePaginatedTeams<FetchCacheTeamsFilterQueryResponse>({
      cacheKey: `${teamType}-teams`,
      query: FETCH_CACHE_TEAMS_FILTER_QUERY,
      initialFilters: { teamType: { eq: teamType } },
      initialLimit: 100
    });

  const {
    teams: selectedTeams,
    teamsError: selectedTeamsError,
    teamsFetched: selectedTeamsFetched,
    teamsLoading: selectedTeamsLoading,
    filterTeams: filterSelectedTeams
  } = useCachePaginatedTeams<FetchCacheTeamsFilterQueryResponse>({
    cacheKey: `selected-${teamType}-teams`,
    query: FETCH_CACHE_TEAMS_FILTER_QUERY,
    initialFilters: { teamType: { eq: teamType }, uuid: { in: value } },
    initialLimit: 100
  });

  const selectedValue = useMemo(() => {
    return selectedTeams?.map(({ uuid: value, name: label }) => ({
      value,
      label
    }));
  }, [selectedTeams]);

  useEffect(() => {
    if (isEqual(value, map(selectedTeams, 'uuid')) || selectedTeamsLoading) {
      return;
    }

    filterSelectedTeams({ uuid: { in: value } });
  }, [value, filterSelectedTeams, selectedTeams, selectedTeamsLoading]);

  const handleFilterTeams = useCallback(
    (teamName: string) =>
      filterTeams({
        teamType: { eq: teamType },
        name: { ilike: teamName }
      }),
    [filterTeams, teamType]
  );

  const data = useMemo(() => {
    return teams?.map(({ uuid: value, name: label }) => ({ value, label }));
  }, [teams]);

  return {
    data,
    handleFilterTeams,
    selectedTeamsFetched,
    teamsLoading,
    selectedValue,
    errorMessage: teamsError || selectedTeamsError
  };
}

export default useTeamsFilter;
